import { useKeyboard } from '@react-native-community/hooks'
import { isError } from '@tanstack/react-query'
import { ImpactFeedbackStyle } from 'expo-haptics'
import * as Network from 'expo-network'
import { Magic, RPCError, RPCErrorCode } from 'magic-sdk'
import { Fragment, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Keyboard, Platform } from 'react-native'

import { useToast } from '../toast'
import { useAuth } from '../useAuth'

import { ENV, USE_OTP_VERiFICATION_FOR_SOME_USERS, WEB } from '~constants'
import { impactAsync, resolveInviteTokenRequest } from '~services'
import { SignInFormValues } from '~types/authForms'
import { noop } from '~utils'

const INVITE_PARAM = 'invite'

const getInviteTokenFromUrl = (): string | undefined => {
  if (Platform.OS !== WEB || typeof window === 'undefined') {
    return undefined
  }
  try {
    const token = new URLSearchParams(window.location.search).get(INVITE_PARAM)
    return token || undefined
  } catch {
    return undefined
  }
}

const defaultValues: SignInFormValues = {
  email: '',
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let magic: any

const emailsAllowedByOTP = [
  'hari@yopmail.com',
  'michal.b@rocketfuelblockchain.com',
  'jhonlee@mailinator.com',
  'blessingworld007@gmail.com',
  'knightbrief@gmail.com',
  'arvind.v3rma@gmail.com',
  'anuj.r@rocketfuelblockchain.com',
]

if (Platform.OS !== WEB) {
  import('@magic-sdk/react-native')
    .then(({ Magic }) => {
      magic = new Magic(ENV.MAGIC_PUBLISHABLE_KEY)
    })
    .catch(noop)
} else {
  import('magic-sdk')
    .then(({ Magic }) => {
      magic = new Magic(ENV.MAGIC_PUBLISHABLE_KEY)
    })
    .catch(noop)
}

export const useSignInForm = () => {
  const { showToast } = useToast()
  const { t } = useTranslation()
  const { keyboardShown } = useKeyboard()

  const { signIn, signInWithOtp } = useAuth()

  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [isResolvingInvite, setIsResolvingInvite] = useState<boolean>(() => {
    return Platform.OS === WEB && !!getInviteTokenFromUrl()
  })
  const enrollmentSourceRef = useRef<string | undefined>(undefined)

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm<SignInFormValues>({
    mode: 'onTouched',
    defaultValues,
  })

  useEffect(() => {
    if (error) {
      showToast({ description: error, variant: 'danger' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- showToast as deps create infinitive loop
  }, [error])

  // Resolve encrypted invite token → autofill email + source; track landing
  useEffect(() => {
    if (Platform.OS !== WEB) return
    const inviteToken = getInviteTokenFromUrl()
    if (!inviteToken) {
      setIsResolvingInvite(false)
      return
    }

    let cancelled = false
    setIsResolvingInvite(true)
    resolveInviteTokenRequest({ inviteToken, trackLanding: true })
      .then((result) => {
        if (cancelled) return
        if (!result?.valid || !result.email) {
          setError(t('errors.something_went_wrong'))
          return
        }
        setValue('email', result.email, { shouldDirty: false, shouldValidate: true })
        enrollmentSourceRef.current = result.enrollmentSource || 'EMAIL_LINK'
      })
      .catch((e) => {
        if (cancelled) return
        setError(isError(e) ? e.message : t('errors.something_went_wrong'))
      })
      .finally(() => {
        if (!cancelled) {
          setIsResolvingInvite(false)
        }
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- showToast via error effect; t stable enough for invite resolve
  }, [setValue, t])

  const onSubmit = async (data: SignInFormValues) => {
    try {
      setError('')
      setIsSubmitting(true)
      keyboardShown && Keyboard.dismiss()
      const NetworkState = await Network.getNetworkStateAsync()
      if (!NetworkState.isInternetReachable) {
        throw new Error(t('errors.network'))
      }
      const email = data.email.toLowerCase()
      // FIXME: workaround for apple review purpose and to test history data. Remove when BE deliver auto sign in on BE side
      if (USE_OTP_VERiFICATION_FOR_SOME_USERS && emailsAllowedByOTP.includes(email)) {
        await signInWithOtp({ email })
      } else {
        const magicToken = await magic.auth.loginWithEmailOTP({
          email,
        })
        if (magicToken) {
          await signIn({
            email,
            magicToken,
            enrollmentSource: enrollmentSourceRef.current,
          })
        }
      }
    } catch (e) {
      if (e instanceof RPCError) {
        let errorMessage = ''
        switch (e.code) {
          case RPCErrorCode.UserRequestEditEmail:
            break
          case RPCErrorCode.MagicLinkFailedVerification:
            errorMessage = t('errors.magic_link.verification_failed')
            break
          case RPCErrorCode.MagicLinkExpired:
            errorMessage = t('errors.magic_link.link_expired')
            break
          case RPCErrorCode.UserAlreadyLoggedIn:
            errorMessage = t('errors.magic_link.user_already_logged_in')
            break
          case RPCErrorCode.InvalidParams:
            errorMessage = t('errors.magic_link.invalid_params')
            break
          case RPCErrorCode.InternalError:
            if (e.message.includes('Network Error')) {
              errorMessage = t('errors.network')
            } else {
              errorMessage = t('errors.magic_link.something_went_wrong')
            }
            break
          default:
            errorMessage = t('errors.magic_link.something_went_wrong')
            break
        }
        errorMessage && setError(errorMessage)
        return
      }
      if (isError(e)) {
        setError(e.message)
      } else {
        setError(t('errors.something_went_wrong'))
      }
      impactAsync(ImpactFeedbackStyle.Medium)
    } finally {
      setIsSubmitting(false)
    }
  }
  return {
    control,
    error,
    errors,
    isSubmitting,
    isResolvingInvite,
    MagicLink: magic?.Relayer || Fragment,
    submit: handleSubmit(onSubmit),
  }
}
